import { Component, OnInit, Input, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { FormGroup, Validators, FormBuilder, AbstractControl } from '@angular/forms';

import * as Utils from '@shared/core/utils';
import { ICellVerifyForm } from './cell-verify.shared.interface';
import { Subscription, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
    selector: 'shared-cell-verify',
    templateUrl: './cell-verify.shared.component.html',
    styleUrls: ['./cell-verify.shared.component.scss']
})
export class CellVerifyComponent implements OnInit {
    private _isEmittingComplete: boolean = false;
    private _d1prev: any;
    private _d2prev: any;
    private _d3prev: any;
    private _d4prev: any;

    public locked: boolean = true;
    @Input() public name: string = 'cell-verify';
    @Input() public invalid: boolean = false; /* Global - can be set from outside ie. when validation request failed */

    @Output() public onChange: EventEmitter<ICellVerifyForm> = new EventEmitter();
    @Output() public onComplete: EventEmitter<ICellVerifyForm> = new EventEmitter();

    @ViewChild('d1', { static: true }) public d1!: ElementRef;
    @ViewChild('d2', { static: true }) public d2!: ElementRef;
    @ViewChild('d3', { static: true }) public d3!: ElementRef;
    @ViewChild('d4', { static: true }) public d4!: ElementRef;

    public form: FormGroup;
    public formListener$: Subscription;

    public d1Listener$: Subscription;
    public d2Listener$: Subscription;
    public d3Listener$: Subscription;
    public d4Listener$: Subscription;

    constructor(
        public formBuilder: FormBuilder,
    ) {
        this.form = this.formBuilder
            .group({
                d1: this._fieldObj(false),
                d2: this._fieldObj(),
                d3: this._fieldObj(),
                d4: this._fieldObj(),
            });
    }

    private _fieldObj(isDisabled: boolean = true): any[] {
        return [
            {
                value: '',
                disabled: isDisabled
            },
            Validators.compose([
                Validators.required,
                Validators.minLength(1),
                Validators.maxLength(1),
            ])
        ];
    }

    ngOnInit() {
        this.formListener$ = this.form.valueChanges
            .subscribe(formValues => {
                if (this.invalid) {
                    this.invalid = false;
                }

                if (this.form.valid) {
                    return this.onChange.emit(this._generateObj(true));
                }
                this.onChange.emit(this._generateObj(false));
            });

        this.d1Listener$ = this._handleNextField('d1', 'd2');
        this.d2Listener$ = this._handleNextField('d2', 'd3');
        this.d3Listener$ = this._handleNextField('d3', 'd4');
        this.d4Listener$ = this._handleNextField('d4', null);
    }

    private _generateObj(isValid: boolean = true): ICellVerifyForm {
        const objectValues = Utils.Forms.extractValues(this.form);

        return isValid ? {
            isValid: true,
            value: Object.keys(objectValues).map(key => objectValues[key]).join(''), /* *** IE doesn't support Object.values method *///Object.values(formValues).join(''),
        } : {
                isValid: false,
                value: null,
            };
    }

    private _handleNextField(current: string, next: string): Subscription {
        const nextField: AbstractControl = next !== null ? this.form.get(next) : null;

        return this._getFieldChangeListener$(current)
            .subscribe(control => {
                if (nextField) {
                    /* d1, d2, d3 case */
                    if (control.valid && control.value !== this[`_${current}prev`]) {
                        if (!nextField.enabled) {
                            nextField.enable();
                        }
                        this[`_${current}prev`] = control.value;
                        this[next].nativeElement.focus();
                        this[next].nativeElement.select();
                    } else {
                        nextField.disable();
                    }
                } else {
                    /* d4 case */
                    if (control.valid && control.value !== this[`_${current}prev`]) {
                        const currentInput: HTMLInputElement = this[current].nativeElement;
                        this[`_${current}prev`] = control.value;
                        currentInput.blur();
                        this._emitComplete();
                    }
                }
            });

    }

    private _getFieldChangeListener$(fieldName: string): Observable<AbstractControl> {
        const abstractControl: AbstractControl = this.form.get(fieldName);
        return abstractControl.valueChanges
            .pipe(
                map(value => abstractControl),
            );
    }

    private _emitComplete(): void {
        if (this._isEmittingComplete) return;
        this._isEmittingComplete = true;

        setTimeout(() => {
            this.onComplete.emit(this._generateObj(true));
            this._isEmittingComplete = false;
        }, 1);
    }

    public onFocus(element: HTMLInputElement): void {
        element.select();
    }

    public onKeyPress(value: string, tabindex: number) {
        if (tabindex > 1) {
            this[`d${tabindex}`].nativeElement.blur();
        }
        if (!value) {
            /* Go back and clear previous values */

            Object.keys(this.form.controls).forEach((key, index) => {
                const curr: number = index + 1;
                /* Clear next */
                if (curr > tabindex) {
                    this.form.get(`d${curr}`).reset('');
                    this.form.get(`d${curr}`).disable();
                }
            });

            if (+tabindex > 1) {
                this[`d${+tabindex - 1}`].nativeElement.focus();
            }
        } else {
            /* Go next */
            const next: number = +tabindex + 1;

            if (next < 5) {
                this.form.get(`d${next}`).enable();
                this[`d${next}`].nativeElement.focus();
            } else {
                /* focus on verify btn */
                this._emitComplete();
            }
        }
    }
}
